"use client";

import { RefreshCw } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useId,
  useState,
  type ChangeEvent,
} from "react";

export type CaptchaFieldHandle = {
  reset: () => void;
};

type Props = {
  autoFocus?: boolean;
  value: string;
  onChange: (value: string) => void;
  onClearError?: () => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  refreshLabel?: string;
  className?: string;
};

const CaptchaField = forwardRef<CaptchaFieldHandle, Props>(
  (
    {
      autoFocus = false,
      value,
      onChange,
      onClearError,
      disabled = false,
      error,
      placeholder = "Enter captcha",
      refreshLabel = "Refresh captcha",
      className,
    },
    ref,
  ) => {
    const [captchaImage, setCaptchaImage] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const requestId = useId();

    const loadCaptcha = async () => {
      setIsRefreshing(true);
      try {
        const res = await fetch(`/api/captcha?rid=${requestId}-${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          credentials: "same-origin",
        });
        if (!res.ok) {
          throw new Error("Failed to load captcha");
        }
        const blob = await res.blob();
        const image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        setCaptchaImage(image);
      } catch (error) {
        console.error("Captcha refresh failed", error);
        setCaptchaImage(null);
      } finally {
        setIsRefreshing(false);
      }
    };

    const refresh = () => {
      onChange("");
      onClearError?.();
      void loadCaptcha();
    };

    useImperativeHandle(ref, () => ({
      reset: refresh,
    }));

    useEffect(() => {
      void loadCaptcha();
    }, []);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value.toUpperCase();
      onChange(nextValue);
      if (nextValue) {
        onClearError?.();
      }
    };

    const imageSrc = captchaImage ? captchaImage : undefined;

    return (
      <div className={className}>
        <div className="space-y-3 rounded-md border border-gray-200 bg-white p-3">
          <div className="flex items-center gap-3">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Captcha challenge"
                className="h-14 w-[150px] rounded border border-gray-200 bg-slate-50"
              />
            ) : (
              <div className="flex h-14 w-[150px] items-center justify-center rounded border border-gray-200 bg-slate-50 text-xs text-gray-500">
                {isRefreshing ? "Loading..." : "Captcha unavailable"}
              </div>
            )}
            <button
              type="button"
              onClick={refresh}
              disabled={disabled || isRefreshing}
              className="text-slateCustom hover:border-slateCustom inline-flex h-10 w-10 items-center justify-center rounded border border-gray-300 transition disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={refreshLabel}
              title={refreshLabel}
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <input
            type="text"
            aria-label={placeholder}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
            autoCapitalize="characters"
            disabled={disabled}
            className="focus:border-slateCustom w-full rounded border border-gray-300 px-3 py-2 text-sm uppercase transition outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
          />
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  },
);

CaptchaField.displayName = "CaptchaField";

export default CaptchaField;
